import { useEffect } from "react";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { OnboardingShell } from "./OnboardingShell";
import { AllSetStep } from "./steps/AllSetStep";
import { EnvironmentCheckStep } from "./steps/EnvironmentCheckStep";
import { InstallStep } from "./steps/InstallStep";
import { ProvisionStep } from "./steps/ProvisionStep";
import { ServicesStep } from "./steps/ServicesStep";
import { WelcomeStep } from "./steps/WelcomeStep";

/**
 * Root of the first-run setup flow.
 *
 * Rendered by App.tsx when the user is authenticated but ``setup_complete``
 * is false. Manages which step is on screen and routes around the Install
 * step automatically when there's nothing to install.
 */
export function OnboardingFlow() {
  const { step, environment, goTo, installQueue } = useOnboardingStore();
  const canEditGlobal = useSettingsStore((s) => s.canEditGlobal);
  const openrouterReady = useSettingsStore(
    (s) => s.settings.openrouter_api_key?.set === true,
  );
  // Shared service config, provisioned or already configured.
  const servicesReady = useSettingsStore(
    (s) =>
      s.settings.opensearch_username?.set === true &&
      s.settings.opensearch_password?.set === true &&
      s.settings.nbyula_gateway_api_key?.set === true,
  );

  // If the environment is already clean when the user reaches the Install
  // step, jump straight past to provisioning — no point showing an empty
  // install screen.
  useEffect(() => {
    if (step !== "install") return;
    if (!environment) return;
    if (installQueue.length === 0 && environment.ready) {
      goTo("provision", true);
    }
  }, [step, environment, installQueue.length, goTo]);

  // Services (opensearch, gateway) are SHARED workspace config. A non-admin on
  // cloud can't write them at all (the backend 403s), and provisioning now
  // hands every install the org's values — so the step only has a job when
  // something is genuinely missing AND this user may set it.
  useEffect(() => {
    if (step !== "services") return;
    if (!canEditGlobal || servicesReady) {
      goTo("done", true);
    }
  }, [step, canEditGlobal, servicesReady, goTo]);

  // Zero-config credentials (ADR-0014): a key already on file means the
  // provisioning step has nothing left to do — skip it. The cloud mints at
  // login; a desktop fills the same set from the cloud right after login
  // (auth_routes). Only a genuinely absent key stops here, and even then the
  // step provisions rather than asks.
  useEffect(() => {
    if (step === "provision" && openrouterReady) {
      goTo("services", true);
    }
  }, [step, openrouterReady, goTo]);

  const showDots = step !== "done";

  let body: React.ReactNode;
  switch (step) {
    case "welcome":
      body = <WelcomeStep />;
      break;
    case "env":
      body = <EnvironmentCheckStep />;
      break;
    case "install":
      body = <InstallStep />;
      break;
    case "provision":
      body = <ProvisionStep />;
      break;
    case "services":
      body = <ServicesStep />;
      break;
    case "done":
      body = <AllSetStep />;
      break;
  }

  return <OnboardingShell showDots={showDots}>{body}</OnboardingShell>;
}
